import cx from 'classnames';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';

type OverflowContainerProps = {
  className?: string;
};

const indicatorStates = {
  NONE: 'NONE',
  RIGHT: 'RIGHT',
  LEFT: 'LEFT',
  BOTH: 'BOTH'
};

const OverflowContainer = ({
  className = '',
  children
}: PropsWithChildren<OverflowContainerProps>) => {
  const itemsRef = useRef<HTMLDivElement>(null);
  const [indicatorState, setIndicatorState] = useState(indicatorStates.NONE);

  const updateIndicatorState = () => {
    if (!itemsRef.current) {
      return;
    }

    let newIndicatorState = indicatorStates.NONE;
    const SCROLL_OFFSET = 30;
    const { scrollWidth, offsetWidth, scrollLeft } = itemsRef.current;
    const maxScroll = scrollWidth - offsetWidth;

    const showLeft = scrollLeft > SCROLL_OFFSET;
    const showRight = maxScroll - scrollLeft > SCROLL_OFFSET;

    if (showRight && showLeft) {
      newIndicatorState = indicatorStates.BOTH;
    } else if (showRight) {
      newIndicatorState = indicatorStates.RIGHT;
    } else if (showLeft) {
      newIndicatorState = indicatorStates.LEFT;
    }

    if (newIndicatorState !== indicatorState) {
      setIndicatorState(newIndicatorState);
    }
  };

  useEffect(() => {
    updateIndicatorState();
    const element = itemsRef.current;

    if (element) {
      element.addEventListener('scroll', updateIndicatorState);
      window.addEventListener('resize', updateIndicatorState);

      return () => {
        element.removeEventListener('scroll', updateIndicatorState);
        window.removeEventListener('resize', updateIndicatorState);
      };
    }
  });

  const showLeft =
    indicatorState === indicatorStates.BOTH ||
    indicatorState === indicatorStates.LEFT;
  const showRight =
    indicatorState === indicatorStates.BOTH ||
    indicatorState === indicatorStates.RIGHT;

  const indicatorClasses =
    'absolute top-0 h-full w-14 transition-opacity -mt-px from-white pointer-events-none z-10';

  const leftIndicatorClasses = cx(
    indicatorClasses,
    'left-0 -ml-px bg-gradient-to-r',
    {
      'opacity-0': !showLeft,
      'opacity-1': showLeft
    }
  );

  const rightIndicatorClasses = cx(
    indicatorClasses,
    'right-0 -mr-px bg-gradient-to-l',
    {
      'opacity-0': !showRight,
      'opacity-1': showRight
    }
  );

  return (
    <div className={`relative w-full overflow-hidden ${className}`}>
      <span className={leftIndicatorClasses} aria-hidden />
      <div
        className="no-scrollbar-horizontal flex overflow-auto"
        ref={itemsRef}
      >
        {children}
      </div>
      <span className={rightIndicatorClasses} aria-hidden />
    </div>
  );
};

export default OverflowContainer;
